<template>
  <div class="corporate-hero-container">
    <div class="corporate-hero-background">
      <div class="corporate-hero-content">
        <h1 class="mb-16 text-h1">Use your free Sittercity membership</h1>
        <p id="descriptionText" class="mb-16 leading-normal text-grey-body">{{ descriptionText }}</p>
        <CorporateClientComboBox
          ref="employerComboBox"
          class="mb-16"
          :status="status"
          :error-text="missingEmployerError"
          @selected-client="onSelectedClient"
          @employer-input-changed="resetErrorState"
        />
        <Button
          id="mainSignUpCTA"
          value="Sign up for free"
          class="w-full"
          size="medium"
          variant="primary"
          @click="onMainCtaClick"
        />
        <div class="social-proof-section">
          <p class="text-caption">Featured in</p>
          <img
            class="social-proof-img"
            src="@/images/bh-benefits/company-logos.png"
            alt="Forbes, The New York Times, Scary Mommy, Good Morning America"
          />
        </div>
      </div>
      <img
        class="corporate-hero-photo"
        src="@/images/bh-benefits/corporate-hero-photo.svg"
        alt="A little girl riding on a sitter's back"
      />
    </div>
    <div class="care-type-container">
      <Card
        v-for="careType in careTypes"
        :key="careType.title"
        :elevation="4"
        class="care-type-card"
        :class="careType.title === 'Companion care' ? 'companion-care-card' : ''"
      >
        <BabysitterIcon v-if="careType.title === 'Child care'" class="mx-auto" />
        <HousekeepingIcon v-if="careType.title === 'Housekeeping'" class="mx-auto" />
        <DashboardPetCareIcon v-if="careType.title === 'Pet care'" class="mx-auto" />
        <CompanionCareIcon v-if="careType.title === 'Companion care'" class="mx-auto" />
        <h3>{{ careType.title }}</h3>
        <p class="text-grey-body">{{ careType.text }}</p>
      </Card>
    </div>
  </div>
</template>

<script lang="ts">
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import BabysitterIcon from '@/icons/BabysitterIcon.vue';
import HousekeepingIcon from '@/icons/HousekeepingIcon.vue';
import DashboardPetCareIcon from '@/icons/DashboardPetCareIcon.vue';
import CompanionCareIcon from '@/icons/CompanionCareIcon.vue';
import CorporateClientComboBox from '@/apps/bhBenefits/components/CorporateClientComboBox.vue';
import BHBenefitRoutes from '@/lib/constants/bh_benefit_route_names';
import helperStatus from '@/helpers/status';

export default {
  name: 'EmployerSearchHero',
  components: {
    CorporateClientComboBox,
    Button,
    Card,
    BabysitterIcon,
    HousekeepingIcon,
    DashboardPetCareIcon,
    CompanionCareIcon,
  },
  data(): any {
    return {
      selectedCompanySlug: '',
      careTypes: [
        { title: 'Child care', text: 'Get in-home support from experienced nannies and babysitters' },
        { title: 'Housekeeping', text: 'Find cleaning, maintenance, and organization help for your home' },
        { title: 'Pet care', text: 'Get help with feeding, walks, and specialized care for any family pet' },
        { title: 'Companion care', text: 'Find help for aging adults with everyday activities or tasks around the home' },
      ],
      status: helperStatus.Neutral,
      missingEmployerError:
        'You may not be eligible for Premium through employer benefits, please <a href="/registration?member_type=seeker" class="underline">sign up here</a>',
      descriptionText: 'Sign up to activate your free membership ($180/year\xa0value) and connect directly with care near you',
    };
  },
  methods: {
    onSelectedClient({ slug }: { slug: string }) {
      this.selectedCompanySlug = slug || '';
    },
    resetErrorState(): void {
      this.status = helperStatus.Neutral;
    },
    focusEmployerField(): void {
      const input = this.$refs.employerComboBox?.$el?.querySelector?.('input');
      if (input) {
        input.focus();
      }
    },
    signUpClick(): void {
      this.onMainCtaClick();
    },
    onMainCtaClick(): void {
      if (this.selectedCompanySlug) {
        this.goToSignUpPage(this.selectedCompanySlug);
        return;
      }
      this.status = helperStatus.Error;
      this.focusEmployerField();
    },
    goToSignUpPage(slug: string): void {
      if (this.$route.query.leadid) {
        this.$router.push({
          name: BHBenefitRoutes.SIGNUP,
          params: { slug },
          query: { leadid: this.$route.query.leadid },
        });
        return;
      }
      this.$router.push({ name: BHBenefitRoutes.SIGNUP, params: { slug } });
    },
  },
};
</script>

<style scoped>
h1 {
  font-size: 3rem;
}
</style>
